import React, { useEffect, useRef, useState } from 'react';
import { FaUserGraduate } from 'react-icons/fa';
import {
  FiClock,
  FiHelpCircle,
  FiPlus,
  FiSliders,
  FiTrash2,
  FiUser,
  FiX,
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import ChatAssessmentCards from '../components/eddy/ChatAssessmentCards';
import ChatRecommendationCards from '../components/eddy/ChatRecommendationCards';
import ProgrammeDetailDrawer from '../components/eddy/ProgrammeDetailDrawer';
import StudentProfilePanel from '../components/eddy/StudentProfilePanel';
import {
  getEddyUiCopy,
  programmeDisplayName
} from '../components/eddy/eddyBetaUi';
import { surfaceCardClass } from '../components/ui/formUiClasses';
import { useAuth } from '../contexts/AuthContext';
import {
  deleteBetaEddySession,
  getBetaEddySession,
  listBetaEddySessions,
  loadBetaEddyProfile,
  sendBetaEddyMessage
} from '../handlers/EddyBetaHandler';

function messageFromServer(message, sessionId, index, language) {
  return {
    id: `${sessionId}-${message.createdAt || index}-${index}`,
    role: message.role,
    text: message.text,
    createdAt: message.createdAt,
    language,
    ui: message.ui
  };
}

function programmesFromMessage(message) {
  const programmes = new Map();
  for (const artifact of message.ui?.artifacts || []) {
    if (artifact.type === 'programme_candidates') {
      for (const candidate of artifact.data?.candidates || []) {
        programmes.set(candidate.programmeCode, {
          ...candidate,
          admissionYear: artifact.data?.admissionYear
        });
      }
    }
    if (artifact.type === 'programme_details') {
      const overview = artifact.data?.sections?.overview || {};
      const programmeCode = artifact.data?.programmeCode || overview.programmeCode;
      if (programmeCode) {
        programmes.set(programmeCode, {
          ...(programmes.get(programmeCode) || {}),
          ...overview,
          programmeCode,
          admissionYear: artifact.data?.admissionYear,
          sections: artifact.data?.sections || {},
          unavailableSections: artifact.data?.unavailableSections || []
        });
      }
    }
  }
  return [...programmes.values()];
}

function artifactData(message, type) {
  return message.ui?.artifacts?.find((artifact) => artifact.type === type)?.data || null;
}

function formatUpdatedAt(value, language) {
  if (!value) return '—';
  const locale = language === 'tc' ? 'zh-HK' : language === 'sc' ? 'zh-CN' : 'en-HK';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}

function DrawerShell({ title, eyebrow, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[65] flex justify-end bg-black/35 backdrop-blur-[2px]" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <aside className="h-full w-full max-w-sm overflow-y-auto border-l border-edge bg-surface shadow-2xl" role="dialog" aria-modal="true" aria-label={title}>
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-edge bg-surface/95 px-5 py-5 backdrop-blur">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-link">{eyebrow}</p>
            <h2 className="mt-1 text-lg font-semibold text-ink">{title}</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-full border border-edge p-2 text-ink-muted transition hover:bg-surface-muted" aria-label={`Close ${title}`}><FiX aria-hidden /></button>
        </div>
        {children}
      </aside>
    </div>
  );
}

function EddyBeta() {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [responseStyle, setResponseStyle] = useState('quick');
  const [conversationLanguage, setConversationLanguage] = useState(currentUser?.preferred_language || 'en');
  const [activePanel, setActivePanel] = useState(null);
  const [selectedProgramme, setSelectedProgramme] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [deletingSessionId, setDeletingSessionId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [serviceStatus, setServiceStatus] = useState('checking');
  const [errorMessage, setErrorMessage] = useState('');
  const chatScrollRef = useRef(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const copy = getEddyUiCopy(conversationLanguage);
  const pointerKey = currentUser?.User_ID
    ? `eddyBetaLastSession:${currentUser.User_ID}`
    : null;

  useEffect(() => {
    if (!currentUser?.User_ID) return undefined;
    let cancelled = false;

    async function initialize() {
      setServiceStatus('checking');
      setErrorMessage('');
      const [profileResult, sessionResult] = await Promise.allSettled([
        loadBetaEddyProfile(),
        listBetaEddySessions()
      ]);
      if (cancelled) return;

      if (profileResult.status === 'fulfilled') setProfile(profileResult.value);
      const loadedSessions = sessionResult.status === 'fulfilled' ? sessionResult.value : [];
      setSessions(loadedSessions);
      if (sessionResult.status === 'rejected') {
        setServiceStatus('unavailable');
        setErrorMessage(sessionResult.reason?.message || 'Unable to load conversations');
        return;
      }
      setServiceStatus('available');

      const rememberedSessionId = pointerKey ? localStorage.getItem(pointerKey) : null;
      if (!rememberedSessionId || !loadedSessions.some(({ sessionId }) => sessionId === rememberedSessionId)) {
        if (pointerKey) localStorage.removeItem(pointerKey);
        return;
      }

      try {
        const session = await getBetaEddySession(rememberedSessionId);
        if (cancelled) return;
        setCurrentSessionId(session.sessionId);
        setConversationLanguage(session.language || 'en');
        setMessages((session.messages || []).map((message, index) => (
          messageFromServer(message, session.sessionId, index, session.language)
        )));
      } catch {
        if (pointerKey) localStorage.removeItem(pointerKey);
      }
    }

    initialize();
    return () => { cancelled = true; };
  }, [currentUser?.User_ID, pointerKey]);

  useEffect(() => {
    if (messages.length === 0 && !isLoading) {
      chatScrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
      return;
    }
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  useEffect(() => {
    if (!activePanel && !selectedProgramme && messages.length > 0) inputRef.current?.focus();
  }, [activePanel, messages, selectedProgramme]);

  const refreshSessions = async () => {
    try {
      const nextSessions = await listBetaEddySessions();
      setSessions(nextSessions);
      setServiceStatus('available');
      return nextSessions;
    } catch (error) {
      setServiceStatus('unavailable');
      setErrorMessage(error.message || 'Unable to load conversations');
      return null;
    }
  };

  const sendMessage = async (rawText) => {
    const text = rawText.trim();
    if (!text || isLoading) return;

    const styleAtSend = responseStyle;
    const languageAtSend = conversationLanguage;

    setMessages((current) => [...current, {
      id: `${Date.now()}-user`,
      role: 'user',
      text,
      language: languageAtSend
    }]);
    setInput('');
    setIsLoading(true);
    setServiceStatus('working');
    setErrorMessage('');
    inputRef.current?.focus();

    try {
      const response = await sendBetaEddyMessage({
        text,
        sessionId: currentSessionId,
        language: languageAtSend,
        responseStyle: styleAtSend
      });
      setCurrentSessionId(response.sessionId);
      if (pointerKey) localStorage.setItem(pointerKey, response.sessionId);
      setMessages((current) => [...current, {
        id: `${Date.now()}-assistant`,
        role: 'assistant',
        text: response.content,
        language: languageAtSend,
        ui: response.ui || { version: 1, responseStyle: styleAtSend, artifacts: [] }
      }]);
      setServiceStatus('available');
      await refreshSessions();
    } catch (error) {
      if (error.sessionId) {
        setCurrentSessionId(error.sessionId);
        if (pointerKey) localStorage.setItem(pointerKey, error.sessionId);
      }
      if (error.code === 'SESSION_NOT_FOUND') {
        setCurrentSessionId(null);
        if (pointerKey) localStorage.removeItem(pointerKey);
      }
      setErrorMessage(error.message || 'Eddy is temporarily unavailable.');
      setServiceStatus('unavailable');
      if (error.sessionId) await refreshSessions();
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (event) => {
    event?.preventDefault();
    sendMessage(input);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit(event);
    }
  };

  const loadConversation = async (sessionId) => {
    setIsHistoryLoading(true);
    setErrorMessage('');
    try {
      const session = await getBetaEddySession(sessionId);
      setCurrentSessionId(session.sessionId);
      setConversationLanguage(session.language || 'en');
      setMessages((session.messages || []).map((message, index) => (
        messageFromServer(message, session.sessionId, index, session.language)
      )));
      if (pointerKey) localStorage.setItem(pointerKey, session.sessionId);
      setActivePanel(null);
      setServiceStatus('available');
    } catch (error) {
      setErrorMessage(error.message || 'Unable to load the conversation');
      setServiceStatus('unavailable');
      if (error.code === 'SESSION_NOT_FOUND') {
        setCurrentSessionId(null);
        setMessages([]);
        if (pointerKey) localStorage.removeItem(pointerKey);
        await refreshSessions();
      }
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const askAboutProgramme = (programme) => {
    setSelectedProgramme(null);
    const name = programmeDisplayName(programme, conversationLanguage);
    const prompt = conversationLanguage === 'tc'
      ? `請詳細介紹 ${programme.programmeCode} ${name}，包括課程概覽、入學要求、計分方式及現有收生參考。`
      : conversationLanguage === 'sc'
        ? `请详细介绍 ${programme.programmeCode} ${name}，包括课程概览、入学要求、计分方式及现有录取参考。`
        : `Tell me more about ${programme.programmeCode} ${name}, including its overview, requirements, score calculation and available admission reference.`;
    window.setTimeout(() => sendMessage(prompt), 0);
  };

  const startNewChat = () => {
    setMessages([]);
    setCurrentSessionId(null);
    setSelectedProgramme(null);
    setErrorMessage('');
    if (pointerKey) localStorage.removeItem(pointerKey);
    setActivePanel(null);
  };

  const deleteConversation = async (sessionId) => {
    if (!window.confirm(copy.deleteConfirm)) return;
    setDeletingSessionId(sessionId);
    setErrorMessage('');
    try {
      await deleteBetaEddySession(sessionId);
      if (sessionId === currentSessionId) startNewChat();
      await refreshSessions();
    } catch (error) {
      setErrorMessage(error.message || 'Unable to delete the conversation');
    } finally {
      setDeletingSessionId(null);
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden bg-canvas">
      <div className="pointer-events-none absolute -right-20 top-8 h-56 w-56 rounded-full bg-accent/20 blur-3xl" aria-hidden />
      <div className="pointer-events-none absolute -left-16 bottom-24 h-48 w-48 rounded-full bg-primary/10 blur-3xl" aria-hidden />

      <div className="relative mx-auto flex h-[calc(100vh-4rem)] max-w-3xl flex-col px-4 py-5 sm:px-6 sm:py-6">
        <header className={`${surfaceCardClass} mb-4 shrink-0 px-5 py-4 sm:px-6`}>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-4">
              <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-edge bg-accent/20 text-link shadow-sm">
                <FaUserGraduate className="h-6 w-6" aria-hidden />
                <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-surface bg-success" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <h1 className="text-lg font-semibold text-ink sm:text-xl">{t('navigation.eddy')}</h1>
                  <span className="inline-flex items-center rounded-full border border-edge bg-surface-muted px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">{t('eddyBeta.badge')}</span>
                </div>
                <p className="max-w-xl text-sm leading-relaxed text-ink-muted">{copy.subtitle}</p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2 pl-16 sm:pl-0">
              <button type="button" onClick={() => setActivePanel('profile')} className="inline-flex items-center gap-2 rounded-full border border-edge bg-surface-muted px-3 py-2 text-xs font-semibold text-ink-muted transition hover:border-link/30 hover:text-link"><FiUser aria-hidden /> {copy.studentProfile}</button>
              <span className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1.5 text-[10px] font-semibold ${serviceStatus === 'unavailable' ? 'border-danger/20 bg-danger/5 text-danger' : 'border-success/20 bg-success/5 text-success'}`}><span className={`h-2 w-2 rounded-full ${serviceStatus === 'unavailable' ? 'bg-danger' : 'bg-success'}`} /> {serviceStatus === 'working' || serviceStatus === 'checking' ? copy.working : serviceStatus === 'unavailable' ? copy.unavailable : copy.available}</span>
            </div>
          </div>
        </header>

        <section className={`${surfaceCardClass} flex min-h-0 flex-1 flex-col overflow-hidden`} aria-label={t('navigation.eddy')}>
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-edge bg-surface-muted/45 px-3 py-2.5 sm:px-4">
            <div className="flex flex-wrap items-center gap-1">
              <button type="button" onClick={() => setActivePanel('history')} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold text-ink-muted transition hover:bg-surface hover:text-link"><FiClock aria-hidden /> {copy.history}</button>
              <button type="button" onClick={() => setActivePanel('tutorial')} className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-[10px] font-medium text-ink-subtle transition hover:bg-surface hover:text-link"><FiHelpCircle aria-hidden /> {copy.tutorial}</button>
            </div>
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center rounded-full border border-edge bg-surface px-2.5 py-1 text-[9px] font-semibold text-ink-subtle"><span className="hidden md:inline">Conversation ·&nbsp;</span><select aria-label="Conversation language" value={conversationLanguage} onChange={(event) => setConversationLanguage(event.target.value)} className="border-0 bg-transparent text-[9px] font-semibold text-ink focus:outline-none"><option value="en">English</option><option value="tc">繁體中文</option><option value="sc">简体中文</option></select></label>
              <label className="inline-flex items-center gap-1.5 rounded-full border border-edge bg-surface px-2.5 py-1 text-[10px] font-medium text-ink-subtle"><FiSliders aria-hidden /><span className="hidden sm:inline">{copy.response}:</span><select aria-label="Response style" value={responseStyle} onChange={(event) => setResponseStyle(event.target.value)} className="border-0 bg-transparent pr-1 text-[10px] font-semibold text-ink focus:outline-none"><option value="quick">{copy.quick}</option><option value="detailed">{copy.detailed}</option></select></label>
            </div>
          </div>

          <div ref={chatScrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6" aria-live="polite" aria-relevant="additions">
            {messages.length === 0 ? (
              <div className="mx-auto flex min-h-full max-w-xl flex-col items-center justify-center py-6 text-center">
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-edge bg-surface-muted text-link shadow-sm"><FaUserGraduate className="h-7 w-7" aria-hidden /></div>
                <h2 className="text-xl font-semibold text-ink sm:text-2xl">{copy.welcomeTitle} <span aria-hidden>👋</span></h2>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-ink-muted">{copy.welcomeBody}</p>

                <div className="mt-7 w-full">
                  <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-subtle">{copy.suggestedQuestions}</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {copy.suggestions.map(([label, prompt]) => <button key={label} type="button" onClick={() => sendMessage(prompt)} className="rounded-full border border-edge-strong bg-surface px-3.5 py-2 text-xs text-ink-muted shadow-sm transition hover:-translate-y-0.5 hover:border-link/35 hover:text-link">{label}</button>)}
                  </div>
                </div>
              </div>
            ) : (
              <ul className="space-y-4">
                {messages.map((message) => (
                  <li key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {message.role === 'assistant' ? (
                      <div className="max-w-[96%] rounded-2xl border border-edge bg-surface px-4 py-3 shadow-sm sm:max-w-[90%]">
                        <div className="mb-2 flex items-center gap-2">
                          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent/20 text-link"><FaUserGraduate className="h-3.5 w-3.5" aria-hidden /></span>
                          <span className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">EDDY · {message.ui?.responseStyle === 'detailed' ? copy.detailedAnalysis : copy.quickAnswer}</span>
                        </div>
                        <div className="text-sm leading-relaxed text-ink [&_a]:text-link [&_a]:underline [&_li]:ml-5 [&_li]:list-disc [&_ol]:space-y-1 [&_p+p]:mt-2 [&_ul]:space-y-1">
                          <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{message.text}</ReactMarkdown>
                        </div>
                        <ChatAssessmentCards requirementAssessment={artifactData(message, 'requirement_assessment')} scoreRiskAssessment={artifactData(message, 'score_risk_assessment')} stateNotice={message.ui?.stateNotice} language={message.language || conversationLanguage} />
                        <ChatRecommendationCards programmes={programmesFromMessage(message)} onSelect={setSelectedProgramme} language={message.language || conversationLanguage} />
                      </div>
                    ) : (
                      <div className="max-w-[85%] rounded-full border border-primary bg-primary px-5 py-2.5 text-sm leading-relaxed text-primary-foreground shadow-sm">{message.text}</div>
                    )}
                  </li>
                ))}
                {isLoading ? (
                  <li className="flex justify-start">
                    <div className="flex items-center gap-1.5 rounded-2xl border border-edge bg-surface px-4 py-3 shadow-sm"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-link" /><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-link [animation-delay:150ms]" /><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-link [animation-delay:300ms]" /><span className="ml-1 text-xs text-ink-subtle">{copy.checkingData}</span></div>
                  </li>
                ) : null}
              </ul>
            )}
            {isHistoryLoading ? <div className="py-4 text-center text-xs text-ink-subtle">{copy.loadingHistory}</div> : null}
            {errorMessage ? <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-danger/20 bg-danger/5 px-4 py-3 text-xs text-danger"><span>{errorMessage}</span><button type="button" onClick={refreshSessions} className="shrink-0 rounded-full border border-danger/20 px-3 py-1 font-semibold">{copy.retry}</button></div> : null}
            <div ref={bottomRef} />
          </div>

          <form onSubmit={handleSubmit} className="shrink-0 border-t border-edge bg-surface-muted/60 px-3 py-3 sm:px-4">
            <div className="flex items-center gap-2 rounded-full border border-edge-strong bg-surface p-1.5 shadow-sm focus-within:border-link/40 focus-within:ring-2 focus-within:ring-ring/15">
              <input ref={inputRef} type="text" value={input} onChange={(event) => setInput(event.target.value)} onKeyDown={handleKeyDown} placeholder={copy.inputPlaceholder} disabled={isLoading} className="min-w-0 flex-1 rounded-full border-0 bg-transparent px-4 py-2 text-sm text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-0 disabled:opacity-60" autoComplete="off" aria-label="Ask EDDY a question" />
              <button type="submit" disabled={!input.trim() || isLoading} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-transparent bg-primary text-primary-foreground transition-colors hover:bg-primary-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50" aria-label="Send message">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden><path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
              </button>
            </div>
            <p className="mt-2 text-center text-[9px] text-ink-subtle">{copy.betaBoundary}</p>
          </form>
        </section>
      </div>

      {activePanel === 'profile' ? (
        <div className="fixed inset-0 z-[65] flex justify-end bg-black/35 backdrop-blur-[2px]" onMouseDown={(event) => { if (event.target === event.currentTarget) setActivePanel(null); }}>
          <div className="h-full w-full max-w-sm border-l border-edge shadow-2xl"><StudentProfilePanel profile={profile} drawer onClose={() => setActivePanel(null)} language={conversationLanguage} /></div>
        </div>
      ) : null}

      {activePanel === 'history' ? (
        <DrawerShell title={copy.history} eyebrow={copy.previousConsultations} onClose={() => setActivePanel(null)}>
          <div className="space-y-2 p-4">
            <button type="button" onClick={startNewChat} className="mb-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-4 py-2.5 text-xs font-semibold text-primary-foreground hover:bg-primary-hover"><FiPlus aria-hidden /> {copy.newChat}</button>
            {isHistoryLoading ? <p className="py-3 text-center text-xs text-ink-subtle">{copy.loadingHistory}</p> : null}
            {sessions.map((session) => {
              const title = session.title || `${copy.untitledConversation} · ${session.sessionId.slice(-6)}`;
              return (
              <div key={session.sessionId} className={`flex items-center gap-2 rounded-2xl border bg-surface p-2 transition hover:border-link/30 hover:bg-surface-muted/50 ${currentSessionId === session.sessionId ? 'border-link/35 ring-1 ring-link/10' : 'border-edge'}`}>
                <button type="button" onClick={() => loadConversation(session.sessionId)} disabled={isHistoryLoading || deletingSessionId === session.sessionId} className="min-w-0 flex-1 px-2 py-2 text-left disabled:opacity-60">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-link">{session.language?.toUpperCase() || 'EN'} · {session.messageCount} {copy.messages}</p>
                  <p className="mt-1 truncate text-sm font-semibold text-ink">{title}</p>
                  <p className="mt-1 text-[10px] text-ink-subtle">{copy.lastUpdated} · {formatUpdatedAt(session.updatedAt, conversationLanguage)}</p>
                </button>
                <button type="button" onClick={() => deleteConversation(session.sessionId)} disabled={deletingSessionId === session.sessionId} className="rounded-full p-2 text-ink-subtle transition hover:bg-surface hover:text-danger disabled:opacity-40" aria-label={`${copy.deleteConversation}: ${title}`}><FiTrash2 aria-hidden /></button>
              </div>
              );
            })}
            {!isHistoryLoading && sessions.length === 0 ? <p className="py-8 text-center text-xs text-ink-subtle">{copy.noConversations}</p> : null}
          </div>
        </DrawerShell>
      ) : null}

      {activePanel === 'tutorial' ? (
        <DrawerShell title={copy.tutorialTitle} eyebrow={copy.tutorialEyebrow} onClose={() => setActivePanel(null)}>
          <div className="space-y-3 p-5">
            {copy.tutorialSteps.map(([title, body], index) => (
              <div key={title} className="flex gap-3 rounded-2xl border border-edge bg-surface-muted/55 p-4"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">{index + 1}</span><div><p className="text-sm font-semibold text-ink">{title}</p><p className="mt-1 text-xs leading-relaxed text-ink-muted">{body}</p></div></div>
            ))}
            <button type="button" onClick={() => setActivePanel(null)} className="mt-2 w-full rounded-full bg-primary px-4 py-2.5 text-xs font-semibold text-primary-foreground hover:bg-primary-hover">{copy.startExploring}</button>
          </div>
        </DrawerShell>
      ) : null}

      <ProgrammeDetailDrawer programme={selectedProgramme} onClose={() => setSelectedProgramme(null)} onAsk={askAboutProgramme} language={conversationLanguage} />
    </div>
  );
}

export default EddyBeta;
